/**
 * ContentTypeConfigsPage — per-type tone, format & AI prompt context editor.
 *
 * Split-panel layout: left = type browser with category groups + config status;
 * right = focused editor with tone / format / AI context / example titles.
 */

import { useState, useCallback, useEffect, useRef } from 'react';
import {
  Settings2, Search, FileText, Hash, Briefcase, Mail, Check,
  Loader2, X, Mic2, Sparkles, ChevronRight,
} from 'lucide-react';
import { useProject } from '../../../contexts/ProjectContext';
import { ALL_CONTENT_TYPES, CONTENT_TYPE_LABELS, type ContentType } from '../../../api/content';
import {
  useContentTypeConfigs,
  useUpsertTypeConfig,
  useDeleteTypeConfig,
  type ContentTypeConfig,
} from '../../../api/content-type-configs';
import { Button } from '../../../components/ui';
import { DashboardPageLayout } from '../../../components/layout';

// =============================================================================
// Category Definitions
// =============================================================================

type Category = { label: string; types: ContentType[]; icon: React.ComponentType<{ size?: number; className?: string }> };

const CATEGORIES: Category[] = [
  {
    label: 'Social',
    icon: Hash,
    types: ['linkedin_post', 'social_post', 'thread'],
  },
  {
    label: 'Editorial',
    icon: FileText,
    types: ['article', 'guide', 'playbook', 'infographic', 'faq', 'news', 'case_study', 'whitepaper', 'video', 'podcast'],
  },
  {
    label: 'Sales',
    icon: Briefcase,
    types: ['proposal', 'presentation', 'one_pager', 'battle_card'],
  },
  {
    label: 'Events & Ops',
    icon: Mail,
    types: ['email_sequence', 'newsletter', 'press_release', 'webinar', 'slides'],
  },
];

// =============================================================================
// Tag Input
// =============================================================================

function TagInput({
  tags,
  onChange,
}: {
  tags: string[];
  onChange: (next: string[]) => void;
}) {
  const [draft, setDraft] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);

  const addTag = useCallback((raw: string) => {
    const trimmed = raw.trim();
    if (trimmed && !tags.includes(trimmed)) {
      onChange([...tags, trimmed]);
    }
    setDraft('');
  }, [tags, onChange]);

  const onKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' || e.key === ',') {
      e.preventDefault();
      addTag(draft);
    } else if (e.key === 'Backspace' && !draft && tags.length > 0) {
      onChange(tags.slice(0, -1));
    }
  };

  const onBlur = () => {
    if (draft) addTag(draft);
  };

  return (
    <div
      className="min-h-[42px] rounded-lg border border-neutral-200 bg-white px-3 py-2 flex flex-wrap gap-1.5 cursor-text focus-within:ring-1 focus-within:ring-coral-400/30 focus-within:border-coral-400 transition-all"
      onClick={() => inputRef.current?.focus()}
    >
      {tags.map(tag => (
        <span
          key={tag}
          className="inline-flex items-center gap-1 bg-neutral-100 text-neutral-700 text-xs px-2 py-0.5 rounded-md"
        >
          {tag}
          <button
            type="button"
            onClick={e => { e.stopPropagation(); onChange(tags.filter(t => t !== tag)); }}
            className="text-neutral-400 hover:text-neutral-700 transition-colors leading-none"
          >
            <X size={10} />
          </button>
        </span>
      ))}
      <input
        ref={inputRef}
        value={draft}
        onChange={e => setDraft(e.target.value)}
        onKeyDown={onKeyDown}
        onBlur={onBlur}
        placeholder={tags.length === 0 ? 'Add example titles, press Enter…' : ''}
        className="flex-1 min-w-[140px] bg-transparent border-none outline-none text-xs text-neutral-700 placeholder:text-neutral-400"
      />
    </div>
  );
}

// =============================================================================
// Save Status
// =============================================================================

type SaveState = 'idle' | 'saving' | 'saved' | 'error';

function SaveStatusIcon({ state }: { state: SaveState }) {
  if (state === 'saving') return <Loader2 size={14} className="animate-spin text-neutral-400" />;
  if (state === 'saved') return <Check size={14} className="text-emerald-500" />;
  return null;
}

function timeAgo(iso: string): string {
  const diff = Date.now() - new Date(iso).getTime();
  const m = Math.floor(diff / 60000);
  if (m < 1) return 'just now';
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  return `${Math.floor(h / 24)}d ago`;
}

// =============================================================================
// Right Panel Editor
// =============================================================================

function TypeEditor({
  type,
  existing,
  workspaceId,
}: {
  type: ContentType;
  existing: ContentTypeConfig | undefined;
  workspaceId: string | undefined;
}) {
  const [tone, setTone] = useState(existing?.tone_instructions ?? '');
  const [format, setFormat] = useState(existing?.format_guidance ?? '');
  const [aiContext, setAiContext] = useState(existing?.ai_prompt_context ?? '');
  const [exampleTitles, setExampleTitles] = useState<string[]>(existing?.example_titles ?? []);
  const [saveState, setSaveState] = useState<SaveState>('idle');

  const upsert = useUpsertTypeConfig(workspaceId);
  const remove = useDeleteTypeConfig(workspaceId);

  // Sync when config loads/changes
  useEffect(() => {
    setTone(existing?.tone_instructions ?? '');
    setFormat(existing?.format_guidance ?? '');
    setAiContext(existing?.ai_prompt_context ?? '');
    setExampleTitles(existing?.example_titles ?? []);
    setSaveState('idle');
  }, [existing?.id, type]);

  const isDirty =
    tone !== (existing?.tone_instructions ?? '') ||
    format !== (existing?.format_guidance ?? '') ||
    aiContext !== (existing?.ai_prompt_context ?? '') ||
    JSON.stringify(exampleTitles) !== JSON.stringify(existing?.example_titles ?? []);

  const save = async () => {
    if (!workspaceId) return;
    setSaveState('saving');
    try {
      await upsert.mutateAsync({
        contentType: type,
        data: {
          tone_instructions: tone || null,
          format_guidance: format || null,
          ai_prompt_context: aiContext || null,
          example_titles: exampleTitles,
        },
      });
      setSaveState('saved');
      setTimeout(() => setSaveState('idle'), 2500);
    } catch {
      setSaveState('error');
    }
  };

  const reset = async () => {
    if (!existing) return;
    await remove.mutateAsync(type);
    setTone(''); setFormat(''); setAiContext(''); setExampleTitles([]);
    setSaveState('idle');
  };

  const label = CONTENT_TYPE_LABELS[type] ?? type;
  const isConfigured = !!(existing?.tone_instructions || existing?.format_guidance || existing?.ai_prompt_context);

  return (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div className="flex items-start justify-between px-6 py-5 border-b border-neutral-100 flex-shrink-0">
        <div>
          <div className="flex items-center gap-3 mb-1">
            <h2 className="text-lg font-semibold text-neutral-900 leading-tight">{label}</h2>
            {isConfigured && (
              <span className="inline-flex items-center gap-1 text-xs font-medium text-emerald-700 bg-emerald-50 border border-emerald-200 px-2 py-0.5 rounded-full">
                <span className="w-1.5 h-1.5 bg-emerald-500 rounded-full" />
                Configured
              </span>
            )}
          </div>
          <p className="text-xs text-neutral-400">
            {existing?.updated_at
              ? `Last saved ${timeAgo(existing.updated_at)}`
              : 'Never configured — using defaults'}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <SaveStatusIcon state={saveState} />
          {saveState === 'saved' && (
            <span className="text-xs text-emerald-600 font-medium">Saved</span>
          )}
          {saveState === 'error' && (
            <span className="text-xs text-red-500">Failed to save</span>
          )}
        </div>
      </div>

      {/* Body */}
      <div className="flex-1 overflow-y-auto px-6 py-5 space-y-6">
        {/* Tone Instructions */}
        <div>
          <label className="block text-xs font-semibold text-neutral-700 uppercase tracking-wide mb-1.5">
            Tone Instructions
          </label>
          <p className="text-xs text-neutral-400 mb-2">
            Voice and style guidance for this content type.
          </p>
          <textarea
            value={tone}
            onChange={e => setTone(e.target.value)}
            placeholder="e.g. Direct and first-person. No em-dashes. Short sentences. Conversational but authoritative."
            rows={4}
            className="w-full rounded-lg border border-neutral-200 bg-white px-3 py-2.5 text-sm text-neutral-800 placeholder:text-neutral-400 resize-none focus:outline-none focus:ring-1 focus:ring-coral-400/30 focus:border-coral-400 transition-all"
          />
        </div>

        {/* Format Guidance */}
        <div>
          <label className="block text-xs font-semibold text-neutral-700 uppercase tracking-wide mb-1.5">
            Format Guidance
          </label>
          <p className="text-xs text-neutral-400 mb-2">
            Structure, length, and formatting rules.
          </p>
          <textarea
            value={format}
            onChange={e => setFormat(e.target.value)}
            placeholder="e.g. Max 300 words. 3 key points max. End with a question. No bullet points."
            rows={4}
            className="w-full rounded-lg border border-neutral-200 bg-white px-3 py-2.5 text-sm text-neutral-800 placeholder:text-neutral-400 resize-none focus:outline-none focus:ring-1 focus:ring-coral-400/30 focus:border-coral-400 transition-all"
          />
        </div>

        {/* AI Prompt Context */}
        <div>
          <label className="block text-xs font-semibold text-neutral-700 uppercase tracking-wide mb-1.5 flex items-center gap-1.5">
            <Sparkles size={12} className="text-coral-500" />
            AI Prompt Context
          </label>
          <p className="text-xs text-neutral-400 mb-2">
            Injected verbatim into AI generation prompts. Use this for complex type-specific instructions.
          </p>
          <textarea
            value={aiContext}
            onChange={e => setAiContext(e.target.value)}
            placeholder={`This text is injected verbatim into AI generation prompts for ${label} content.\n\nExample:\nWhen writing a ${label.toLowerCase()}, always start with a hook. Consider the audience's pain points before positioning the solution.`}
            rows={6}
            className="w-full rounded-lg border border-neutral-200 bg-neutral-50 px-3 py-2.5 font-mono text-xs text-neutral-800 placeholder:text-neutral-400 placeholder:font-sans resize-none focus:outline-none focus:ring-1 focus:ring-coral-400/30 focus:border-coral-400 transition-all"
          />
        </div>

        {/* Example Titles */}
        <div>
          <label className="block text-xs font-semibold text-neutral-700 uppercase tracking-wide mb-1.5">
            Example Titles
          </label>
          <p className="text-xs text-neutral-400 mb-2">
            Reference titles that capture the right tone for this type.
          </p>
          <TagInput tags={exampleTitles} onChange={setExampleTitles} />
        </div>
      </div>

      {/* Action bar */}
      <div className="flex items-center justify-between px-6 py-4 border-t border-neutral-100 bg-neutral-50/80 flex-shrink-0">
        <div>
          {existing && (
            <button
              type="button"
              onClick={reset}
              disabled={remove.isPending}
              className="text-xs text-neutral-400 hover:text-red-500 transition-colors disabled:opacity-50"
            >
              Reset to defaults
            </button>
          )}
        </div>
        <Button
          onClick={save}
          disabled={saveState === 'saving' || !isDirty}
          leftIcon={saveState === 'saving' ? <Loader2 size={14} className="animate-spin" /> : undefined}
          size="sm"
        >
          {saveState === 'saving' ? 'Saving…' : 'Save'}
        </Button>
      </div>
    </div>
  );
}

// =============================================================================
// Main Page
// =============================================================================

const ContentTypeConfigsPage = () => {
  const { currentProject } = useProject();
  const workspaceId = currentProject?.workspaceId;

  const [selected, setSelected] = useState<ContentType | null>(null);
  const [search, setSearch] = useState('');

  const { data: configs, isLoading } = useContentTypeConfigs(workspaceId);

  const configMap = new Map<string, ContentTypeConfig>(
    (configs ?? []).map(c => [c.content_type, c])
  );

  const isConfigured = (type: ContentType) => {
    const cfg = configMap.get(type);
    return !!(cfg?.tone_instructions || cfg?.format_guidance || cfg?.ai_prompt_context);
  };

  const filteredCategories = CATEGORIES.map(cat => ({
    ...cat,
    types: cat.types.filter(t =>
      !search ||
      CONTENT_TYPE_LABELS[t]?.toLowerCase().includes(search.toLowerCase()) ||
      t.toLowerCase().includes(search.toLowerCase())
    ),
  })).filter(cat => cat.types.length > 0);

  const selectedConfig = selected ? configMap.get(selected) : undefined;

  return (
    <DashboardPageLayout
      title="Type Instructions"
      subtitle="Configure per-type tone, format and AI context for content generation"
      icon={<Settings2 size={20} />}
    >
      <div className="flex gap-0 border border-neutral-200 rounded-xl overflow-hidden bg-white" style={{ minHeight: '600px' }}>

        {/* ================================================================ */}
        {/* LEFT PANEL — type browser                                         */}
        {/* ================================================================ */}
        <div className="flex flex-col border-r border-neutral-200 bg-neutral-50" style={{ width: '240px', flexShrink: 0 }}>
          {/* Search */}
          <div className="px-3 py-3 border-b border-neutral-200">
            <div className="relative">
              <Search size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-neutral-400 pointer-events-none" />
              <input
                value={search}
                onChange={e => setSearch(e.target.value)}
                placeholder="Filter types…"
                className="w-full bg-white border border-neutral-200 rounded-lg pl-8 pr-3 py-1.5 text-xs text-neutral-700 placeholder:text-neutral-400 focus:outline-none focus:ring-1 focus:ring-coral-400/30 focus:border-coral-400 transition-all"
              />
            </div>
          </div>

          {/* Category groups */}
          <div className="flex-1 overflow-y-auto py-2">
            {isLoading ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 size={16} className="animate-spin text-neutral-300" />
              </div>
            ) : filteredCategories.length === 0 ? (
              <div className="text-xs text-neutral-400 text-center py-8 px-4">No types match your search</div>
            ) : (
              filteredCategories.map(cat => {
                const Icon = cat.icon;
                return (
                  <div key={cat.label} className="mb-1">
                    <div className="flex items-center gap-1.5 px-3 py-1.5">
                      <Icon size={11} className="text-neutral-400 flex-shrink-0" />
                      <span className="text-[10px] font-semibold text-neutral-400 uppercase tracking-wider">{cat.label}</span>
                    </div>
                    {cat.types.map(type => {
                      const active = selected === type;
                      const configured = isConfigured(type);
                      return (
                        <button
                          key={type}
                          onClick={() => setSelected(type)}
                          className={[
                            'w-full flex items-center gap-2.5 px-3 py-2 text-left transition-colors group',
                            active
                              ? 'bg-coral-50 text-coral-700'
                              : 'hover:bg-neutral-100 text-neutral-700',
                          ].join(' ')}
                        >
                          {/* Config status dot */}
                          <span
                            className={[
                              'w-1.5 h-1.5 rounded-full flex-shrink-0 transition-colors',
                              configured ? 'bg-emerald-500' : 'bg-neutral-300 group-hover:bg-neutral-400',
                            ].join(' ')}
                          />
                          <span className="flex-1 text-xs truncate font-medium">
                            {CONTENT_TYPE_LABELS[type] ?? type}
                          </span>
                          {active && <ChevronRight size={10} className="text-coral-400 flex-shrink-0" />}
                        </button>
                      );
                    })}
                  </div>
                );
              })
            )}
          </div>

          {/* Footer hint */}
          <div className="px-3 py-2.5 border-t border-neutral-200">
            <div className="flex items-center gap-1.5">
              <span className="w-1.5 h-1.5 bg-emerald-500 rounded-full" />
              <span className="text-[10px] text-neutral-400">Configured</span>
              <span className="w-1.5 h-1.5 bg-neutral-300 rounded-full ml-2" />
              <span className="text-[10px] text-neutral-400">Default</span>
            </div>
          </div>
        </div>

        {/* ================================================================ */}
        {/* RIGHT PANEL — editor                                              */}
        {/* ================================================================ */}
        <div className="flex-1 flex flex-col min-w-0">
          {selected ? (
            <TypeEditor
              key={selected}
              type={selected}
              existing={selectedConfig}
              workspaceId={workspaceId}
            />
          ) : (
            <div className="flex-1 flex flex-col items-center justify-center text-center p-12">
              <div className="w-14 h-14 rounded-2xl bg-neutral-50 border border-neutral-200 flex items-center justify-center mb-4">
                <Mic2 size={22} className="text-neutral-300" />
              </div>
              <h3 className="text-sm font-semibold text-neutral-700 mb-1.5">Select a content type</h3>
              <p className="text-xs text-neutral-400 max-w-xs leading-relaxed">
                Choose a type from the left panel to configure its tone, format rules, and AI prompt context.
              </p>
              {!search && (
                <div className="mt-6 grid grid-cols-3 gap-2 max-w-xs">
                  {(['linkedin_post', 'podcast', 'proposal'] as ContentType[]).map(t => (
                    <button
                      key={t}
                      onClick={() => setSelected(t)}
                      className="text-xs text-neutral-500 bg-neutral-50 hover:bg-neutral-100 border border-neutral-200 rounded-lg px-2 py-1.5 transition-colors"
                    >
                      {CONTENT_TYPE_LABELS[t]}
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </DashboardPageLayout>
  );
};

export default ContentTypeConfigsPage;
